import React from 'react';
import { Link, LinkProps } from 'react-router-dom';
import clsx from 'clsx';

export type LogoType = 'logo+icon' | 'logo' | 'favicon' | 'icon';
export type LogoVariant = 'full' | 'workspaces' | 'concierge';

// --- Config ---
const sizeConfig = {
  sm: { icon: 'h-5 w-5', text: 'text-lg', gap: 'gap-1', badge: 'text-[9px]', dotSize: '0.15em', dotTop: '-0.06em' },
  md: { icon: 'h-7 w-7', text: 'text-2xl', gap: 'gap-1.5', badge: 'text-[10px]', dotSize: '0.16em', dotTop: '-0.07em' },
  lg: { icon: 'h-9 w-9', text: 'text-4xl', gap: 'gap-2', badge: 'text-[11px]', dotSize: '0.17em', dotTop: '-0.08em' },
};

export interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  logoType?: LogoType;
  variant?: LogoVariant;
  to?: LinkProps['to'];
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  className?: string;
  'aria-label'?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  theme?: 'dark' | 'light';
}

export const Logo: React.FC<LogoProps> = ({
  size = 'md',
  logoType = 'logo+icon',
  variant,
  to,
  href,
  target,
  rel,
  className,
  'aria-label': ariaLabel = 'Dopomogai Logo',
  onClick,
  theme = 'dark',
}) => {
  const isIconOnly = logoType === 'favicon' || logoType === 'icon';
  const showText = !isIconOnly || !!variant;
  const isInteractive = !!(to || href || onClick);

  const isDark = theme === 'dark';

  // 1. Text Colors
  const textColor = isDark ? 'text-white' : 'text-slate-900';
  const subTextColor = isDark ? 'text-slate-400' : 'text-slate-500';

  // 2. Dot Color Logic (Yellow in Dark Mode, Blue in Light Mode)
  // 'bg-blue-600' is a nice vibrant blue that matches the darker petals
  const dotColor = isDark ? 'bg-yellow-400' : 'bg-blue-600';

  const styles = sizeConfig[size];
  const wrapperClassName = clsx(
    'inline-flex rounded-md',
    'appearance-none border-0 bg-transparent p-0 text-inherit',
    isInteractive && 'cursor-pointer',
    isInteractive && 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500',
  );

  const LogoContent = (
    <div className={clsx("flex items-center select-none font-sans", styles.gap, className)}>
      {/* THE ICON */}
      <img
        src="/images/logos/logo-mark.svg"
        alt={showText ? '' : ariaLabel}
        aria-hidden={showText ? 'true' : undefined}
        className={clsx("object-contain", styles.icon)}
        draggable={false}
      />

      {/* THE TEXT */}
      {showText && (
        <div className="flex flex-col justify-center leading-none">
          <div className={clsx("font-semibold tracking-tight flex items-baseline", textColor, styles.text)}>
            <span>Dopomoga</span>
            <span className="relative inline-flex leading-none">
              {'\u0131'}
              <span
                className={clsx("absolute rounded-full", dotColor)}
                style={{
                  left: '50%',
                  top: styles.dotTop,
                  width: styles.dotSize,
                  height: styles.dotSize,
                  transform: 'translateX(-52%)',
                }}
                aria-hidden="true"
              />
            </span>
          </div>

          {/* BADGES */}
          {variant === 'workspaces' && (
            <span className={clsx("uppercase tracking-[0.2em] font-medium mt-0.5", subTextColor, styles.badge)}>
              Workspaces
            </span>
          )}
          {variant === 'concierge' && (
            <span className={clsx("uppercase tracking-[0.2em] font-medium mt-0.5 text-emerald-500", styles.badge)}>
              Concierge
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        aria-label={ariaLabel}
        className={wrapperClassName}
      >
        {LogoContent}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        aria-label={ariaLabel}
        className={wrapperClassName}
      >
        {LogoContent}
      </a>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        aria-label={ariaLabel}
        className={wrapperClassName}
      >
        {LogoContent}
      </button>
    );
  }

  return (
    <div
      className={clsx("inline-flex rounded-md")}
      role={isIconOnly ? 'img' : undefined}
      aria-label={isIconOnly ? ariaLabel : undefined}
    >
      {LogoContent}
    </div>
  );
};
